/**
 * Pure 24-hour time-entry helpers shared by TimeEntryInput.
 * No React / CSS deps — safe to unit-test from any package.
 */

export const HH_MM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

function twoDigits(value: number): string {
  return String(value).padStart(2, '0');
}

function canonicalTime(hours: number, minutes: number): string | null {
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return `${twoDigits(hours)}:${twoDigits(minutes)}`;
}

/**
 * Parse free-text time entry to canonical 24-hour `HH:MM`.
 * Never infers AM/PM. Unparseable → `null`. Empty → `null`.
 */
export function parseTimeInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const compact = trimmed.replace(/\s/g, '');
  if (!compact) return null;

  if (!/^[\d:]+$/.test(compact)) return null;

  if (compact.includes(':')) {
    const parts = compact.split(':');
    if (parts.length !== 2) return null;
    const [hoursPart, minutesPart] = parts;
    if (!hoursPart || !minutesPart) return null;
    if (!/^\d+$/.test(hoursPart) || !/^\d+$/.test(minutesPart)) return null;
    return canonicalTime(Number(hoursPart), Number(minutesPart));
  }

  const digits = compact;
  if (digits.length > 4) return null;

  // A bare hour is ACCEPTED and normalised to the top of that hour.
  //
  // An earlier revision refused it, to stop "3" silently becoming 03:00 when a
  // roster read as "7 to 3" means 15:00. That traded a silent wrong value for a
  // silent REFUSAL, which turned out worse: on the create-shift form a
  // coordinator typed "7", tabbed on, and the field kept her text without ever
  // producing a value, so the form said something was missing and shift
  // creation was blocked with no explanation at the point of entry.
  //
  // The original defect was never the conversion — it was that an AM/PM picker
  // could be mis-set INVISIBLY. Here the field is 24-hour and normalises in
  // place on blur, so "7" visibly becomes "07:00" and anyone who meant 3pm can
  // see they need 15:00. Shown, not silent, is the property that matters.
  if (digits.length === 1 || digits.length === 2) {
    return canonicalTime(Number(digits), 0);
  }

  if (digits.length === 3) {
    return canonicalTime(Number(digits.slice(0, 1)), Number(digits.slice(1, 3)));
  }

  return canonicalTime(Number(digits.slice(0, 2)), Number(digits.slice(2, 4)));
}

/**
 * Sanitise keystrokes for live typing. Deliberately does NOT insert a colon.
 *
 * An earlier version placed one after the second digit as soon as three digits
 * were present. That contradicts `parseTimeInput`, which reads three digits as
 * H:MM — so "930" became "93:0", and on blur parsed as 93 hours and failed.
 * "730" failed the same way. Those are ordinary morning start times, so the
 * control rejected exactly the input it exists to accept.
 *
 * Auto-inserting correctly would mean moving the colon as the user types (after
 * the 1st digit at three, the 2nd at four), which jumps under the cursor. It is
 * simpler and safer to accept what was typed and canonicalise once on blur,
 * where `parseTimeInput` is the single authority on meaning.
 *
 * Keeps digits and at most one colon, capped at "HH:MM" length.
 */
export function formatPartialTimeInput(raw: string): string {
  let seenColon = false;
  let out = '';
  for (const char of raw) {
    if (char >= '0' && char <= '9') {
      out += char;
    } else if (char === ':' && !seenColon && out.length > 0) {
      seenColon = true;
      out += char;
    }
    if (out.length >= 5) break;
  }
  return out;
}

/**
 * Validation message for the time field.
 * `null` when valid or (empty and not required); otherwise a short message.
 */
export function timeEntryValidationMessage(
  raw: string,
  opts?: { required?: boolean },
): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return opts?.required ? 'Enter a time.' : null;
  }
  if (parseTimeInput(trimmed) !== null) return null;
  return 'Enter a valid time (HH:MM), e.g. 0900 or 1500.';
}
